import {
  Button,
  Chip,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownTrigger,
  ScrollShadow,
  Spinner,
} from '@heroui/react'
import type { PlanWithBillingDto, QpaServiceResponseDto } from '@qse/api'
import { usePlans } from '@qse/api'
// Import the correct plan response type
import { type PlanResponseDto, useActivatePlan, useDeactivatePlan, useDeletePlan } from '@qse/api'
import { type Column, ModalData, ModelControlEnum, UserRoleEnum } from '@qse/constants'
import { useActiveModelStore, useUserStore } from '@qse/stores'
import { ErrorData, LoadingData, TableComponent, toast } from '@qse/ui'
import { useIsMobile, usePageFocusRefetch, useURLParams } from '@qse/utils'
import { ChevronDownIcon } from 'lucide-react'
import moment from 'moment'
import React, { Key, useMemo, useRef, useState } from 'react'

// Utility function to format storage
const formatStorageQuota = (storageGb: number) => {
  if (storageGb >= 1024) {
    return `${(storageGb / 1024).toFixed(1)} TB`
  }

  return `${storageGb} GB`
}

type LoadingAction = 'activate' | 'deactivate' | 'delete' | null

type TableRow = (PlanResponseDto | PlanWithBillingDto) & {
  __rowLoading: LoadingAction
}
interface PlanCardProps {
  plan: PlanResponseDto | PlanWithBillingDto
  onEdit: (action: string, plan?: PlanResponseDto | PlanWithBillingDto) => void
  onDelete: (plan: PlanResponseDto | PlanWithBillingDto) => void
  onActivate: (plan: PlanResponseDto | PlanWithBillingDto) => void
  onDeactivate: (plan: PlanResponseDto | PlanWithBillingDto) => void
  userRole: string
  loadingPlanId: string | null
  loadingAction: LoadingAction
}

const columns: Column[] = [
  { name: 'Plan Name', uid: 'name', sortable: true },
  { name: 'Storage Limit', uid: 'storageQuotaGb', sortable: false },
  { name: 'API Limit', uid: 'serviceUsageQuota', sortable: false },
  { name: 'Status', uid: 'status', sortable: true },
  { name: 'Plan Audience', uid: 'audience', sortable: true },
  { name: 'Stripe Plan', uid: 'productName', sortable: false },
  { name: 'QPA License', uid: 'qpaSeedCount', sortable: false },
  { name: 'QPA Services', uid: 'qpaServices', sortable: false },
  { name: 'Created Date', uid: 'createdAt', sortable: false },
  { name: '', uid: 'actions' },
]
const PlanCard = ({
  plan,
  onEdit,
  onDelete,
  onActivate,
  onDeactivate,
  userRole,
  loadingPlanId,
  loadingAction,
}: PlanCardProps) => {
  const isLoading = loadingPlanId === plan.id

  return (
    <div className="bg-white dark:bg-[#1A1A1A] rounded-lg border border-gray-200 dark:border-[#2A2A2A] p-4 shadow relative">
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
          <p className="text-base font-medium dark:text-white text-black">{plan.name}</p>
          <p className="text-sm dark:text-[#AFAFAF] text-[#262626]">id: {plan.id}</p>
        </div>
        <Dropdown
          classNames={{
            content: 'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
          }}
        >
          <DropdownTrigger>
            <Button isIconOnly size="sm" variant="light">
              <span className="text-2xl">⋮</span>
            </Button>
          </DropdownTrigger>
          <DropdownMenu>
            <DropdownItem key="edit" onPress={() => onEdit('edit', plan)}>
              Edit
            </DropdownItem>
            {!plan.isActive && userRole === UserRoleEnum.SUPER_ADMIN ? (
              <DropdownItem
                key="delete"
                isDisabled={isLoading && loadingAction === 'deactivate'}
                onPress={() => onDelete(plan)}
              >
                Delete
              </DropdownItem>
            ) : null}
            {plan.isActive ? (
              <DropdownItem
                key="deactivate"
                isDisabled={isLoading && loadingAction === 'deactivate'}
                onPress={() => onDeactivate(plan)}
              >
                Deactivate
              </DropdownItem>
            ) : (
              <DropdownItem
                key="activate"
                isDisabled={isLoading && loadingAction === 'activate'}
                onPress={() => onActivate(plan)}
              >
                Activate
              </DropdownItem>
            )}
          </DropdownMenu>
        </Dropdown>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <div className="font-light text-sm">Storage Limit</div>
          <div className="font-medium">{formatStorageQuota(plan.storageQuotaGb)}</div>
        </div>

        <div>
          <div className="font-light text-sm">API Limit</div>
          <div className="font-medium">{plan.serviceUsageQuota.toLocaleString()}</div>
        </div>
        <div>
          <div className="font-light text-sm">QPA License Count</div>
          <div className="font-medium">{plan.qpaSeedCount?.toLocaleString() || 'N/A'}</div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <div className="font-light text-sm">Status</div>
          <Chip
            className="capitalize rounded font-medium"
            size="sm"
            variant="flat"
            style={{
              backgroundColor: plan.isActive ? '#378A921A' : '#F5A5241A',
              color: plan.isActive ? '#378A92' : '#F5A524',
            }}
          >
            {plan.isActive ? 'Active' : 'Deactivated'}
          </Chip>
        </div>
        <div>
          <div className="font-light text-sm">Stripe Plan</div>
          <div className="font-medium">
            {'productName' in plan ? String(plan.productName || 'N/A') : 'N/A'}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mt-4">
        <div>
          <div className="font-light text-sm">Created Date</div>
          <div className="font-medium">{moment(plan.createdAt).format('MM/DD/YYYY')}</div>
        </div>
        <div>
          <div className="font-light text-sm">Plan Audience</div>
          <div className="font-medium">{'audience' in plan ? plan.audience : 'N/A'}</div>
        </div>
      </div>
    </div>
  )
}

const PlanManagement = () => {
  const isMobile = useIsMobile()
  const { user } = useUserStore()
  const [loadingPlanId, setLoadingPlanId] = useState<string | null>(null)
  const [loadingAction, setLoadingAction] = useState<LoadingAction>(null)
  // Use ref to track in-flight operations synchronously (prevents double-click race conditions)
  const inFlightOperations = useRef<Set<string>>(new Set())

  // URL parameter management
  const {
    values: urlParams,
    updateParam,
    updateParams,
  } = useURLParams({
    page: { key: 'page', defaultValue: 1 },
    rowsPerPage: { key: 'rowsPerPage', defaultValue: 10 },
    sortBy: { key: 'sortBy', defaultValue: '' },
    sortOrder: { key: 'sortOrder', defaultValue: '' as 'asc' | 'desc' | '' },
  })

  const { page, rowsPerPage, sortBy, sortOrder } = urlParams
  const [sortTimestamp, setSortTimestamp] = useState<number>(Date.now())

  const offset = useMemo(() => (page - 1) * rowsPerPage, [page, rowsPerPage])

  const filters = useMemo(
    () => ({
      limit: rowsPerPage,
      offset: offset,
      sortBy: sortBy ? (sortBy as 'name' | 'status' | 'audience') : undefined,
      sortOrder: sortBy && sortOrder ? (sortOrder as 'asc' | 'desc') : undefined,
      _sortTimestamp: sortTimestamp, // This ensures query key changes when sorting changes
    }),
    [rowsPerPage, offset, sortBy, sortOrder, sortTimestamp],
  )

  const {
    data: plansData,
    isLoading: isPlansLoading,
    error: plansError,
    refetch,
  } = usePlans(filters, {
    enabled: user?.userRole !== UserRoleEnum.TENANT_USER,
  })

  // Use the page focus refetch hook to automatically refetch data
  usePageFocusRefetch(() => {
    if (user?.userRole !== UserRoleEnum.TENANT_USER) {
      refetch()
    }
  })

  const { mutateAsync: mutateAsyncDeletePlan } = useDeletePlan()
  const { mutateAsync: mutateAsyncActivatePlan } = useActivatePlan()
  const { mutateAsync: mutateAsyncDeactivatePlan } = useDeactivatePlan()

  const handlePageChange = (newPage: number) => {
    updateParam('page', newPage, false)
  }

  const handleActivatePlan = (plan: PlanResponseDto | PlanWithBillingDto) => {
    // Prevent double-submit using synchronous ref check (prevents race conditions)
    if (inFlightOperations.current.has(plan.id)) {
      return
    }

    // Mark operation as in-flight immediately (synchronous)
    inFlightOperations.current.add(plan.id)
    setLoadingPlanId(plan.id)
    setLoadingAction('activate')

    mutateAsyncActivatePlan(plan.id, {
      onSuccess: () => {
        toast.success(`Plan '${plan.name}' has been reactivated successfully`)
      },
      onError: () => {
        toast.error(`Failed to reactivate plan '${plan.name}'. Please try again.`)
      },
      onSettled: () => {
        // Remove from in-flight operations
        inFlightOperations.current.delete(plan.id)
        setLoadingPlanId(null)
        setLoadingAction(null)
      },
    })
  }

  const handleDeactivatePlan = (plan: PlanResponseDto | PlanWithBillingDto) => {
    // Prevent double-submit using synchronous ref check (prevents race conditions)
    if (inFlightOperations.current.has(plan.id)) {
      return
    }

    const tenantCount = plan.tenantCount || 0

    // If no tenants, deactivate immediately
    if (tenantCount === 0) {
      // Mark operation as in-flight immediately (synchronous)
      inFlightOperations.current.add(plan.id)
      setLoadingPlanId(plan.id)
      setLoadingAction('deactivate')

      mutateAsyncDeactivatePlan(plan.id, {
        onSuccess: () => {
          toast.success(`Plan '${plan.name}' has been deactivated successfully`)
        },
        onError: () => {
          toast.error(`Failed to deactivate plan '${plan.name}'. Please try again.`)
        },
        onSettled: () => {
          // Remove from in-flight operations
          inFlightOperations.current.delete(plan.id)
          setLoadingPlanId(null)
          setLoadingAction(null)
        },
      })

      return
    }

    // If tenants exist, show confirmation modal
    const config: ModalData = {
      modalHeader: {
        title: 'Deactivate Plan',
        showCloseButton: true,
      },
      icon: '/assets/svg/warning.svg',
      body: () => (
        <div className="flex flex-col gap-3 leading-relaxed break-words text-left">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
            You are about to deactivate the "{plan.name}" plan.
          </h2>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            This plan currently has {tenantCount} active tenant{tenantCount === 1 ? '' : 's'} using
            it. Deactivating this plan will:
          </p>
          <ul className="list-disc ml-6 space-y-1.5 text-gray-700 dark:text-gray-300 [&_li]:text-sm [&_li]:leading-relaxed">
            <li>Hide it from new customer signup and plan switching options</li>
            <li>Keep existing tenants on this plan with full access</li>
            <li>Allow you to reactivate it at any time</li>
          </ul>
          <p className="text-sm text-gray-700 dark:text-gray-300">
            Current tenants will continue to have access to all plan features and will not be
            notified of this change.
          </p>
          <p className="text-base font-medium text-gray-900 dark:text-white mt-1">
            Are you sure you want to deactivate this plan?
          </p>
        </div>
      ),
      classNames: {
        wrapper: 'w-[500px]',
      },
      actions: [
        {
          label: 'Cancel',
          color: 'default',
        },
        {
          label: 'Confirm',
          color: 'primary',
          onPress: async () => {
            // Prevent double-submit using synchronous ref check (prevents race conditions)
            if (inFlightOperations.current.has(plan.id)) {
              return
            }

            // Mark operation as in-flight immediately (synchronous)
            inFlightOperations.current.add(plan.id)
            setLoadingPlanId(plan.id)
            setLoadingAction('deactivate')

            await mutateAsyncDeactivatePlan(plan.id, {
              onSuccess: () => {
                toast.success(`Plan '${plan.name}' has been deactivated successfully`)
              },
              onError: () => {
                toast.error(`Failed to deactivate plan '${plan.name}'. Please try again.`)
              },
              onSettled: () => {
                // Remove from in-flight operations
                inFlightOperations.current.delete(plan.id)
                setLoadingPlanId(null)
                setLoadingAction(null)
              },
            })
          },
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[750px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleRowsPerPageChange = (newLimit: number) => {
    updateParam('rowsPerPage', newLimit)
  }

  const handleSortChange = (sortDescriptor: {
    column: Key
    direction: 'ascending' | 'descending'
  }) => {
    setSortTimestamp(Date.now()) // Update timestamp to force query refetch

    // Extract column - handle various formats
    let clickedColumn: string

    if (typeof sortDescriptor.column === 'string') {
      clickedColumn = sortDescriptor.column
    } else if (
      sortDescriptor.column &&
      typeof sortDescriptor.column === 'object' &&
      'uid' in sortDescriptor.column
    ) {
      clickedColumn = (sortDescriptor.column as any).uid
    } else {
      console.error('Invalid column in sortDescriptor:', sortDescriptor.column)

      return
    }

    const clickedDirection = sortDescriptor.direction === 'ascending' ? 'asc' : 'desc'
    const currentSortBy = sortBy
    const currentSortOrder = sortOrder

    // Implement tri-state sorting: ASC → DESC → Clear → ASC...
    if (currentSortBy === clickedColumn && currentSortBy !== '') {
      // Same column clicked - cycle through states
      if (currentSortOrder === 'desc' && clickedDirection === 'asc') {
        // DESC → Clear (HeroUI tries to cycle back to ASC, we intercept to clear)
        updateParams({
          sortBy: '',
          sortOrder: '',
        })
      } else {
        // ASC → DESC
        updateParams({
          sortBy: clickedColumn,
          sortOrder: clickedDirection,
        })
      }
    } else {
      // Different column clicked OR no sort currently active - set the clicked sort
      updateParams({
        sortBy: clickedColumn,
        sortOrder: clickedDirection,
      })
    }
  }

  // Create sort descriptor for the table component
  const sortDescriptor =
    sortBy && sortBy !== ''
      ? {
          column: sortBy,
          direction: sortOrder === 'asc' ? ('ascending' as const) : ('descending' as const),
        }
      : undefined

  const plans = useMemo(() => plansData?.data || [], [plansData?.data])

  // Calculate pagination based on actual API response format
  const pageInfo = useMemo(() => {
    if (!plansData?.pageInfo) {
      return {
        totalPages: 1,
        count: plans.length,
        hasNextPage: false,
        hasPreviousPage: false,
      }
    }

    const apiPageInfo = plansData.pageInfo

    // Calculate total pages based on available information
    let calculatedTotalPages = 1
    let calculatedCount = plans.length

    if (apiPageInfo.endOffset !== null && apiPageInfo.endOffset !== undefined) {
      // endOffset represents the last item index (0-based)
      // So total items = endOffset + 1
      calculatedCount = apiPageInfo.endOffset + 1
      calculatedTotalPages = Math.ceil(calculatedCount / rowsPerPage)
    }

    // If hasNextPage is true, ensure we show at least one more page
    if (apiPageInfo.hasNextPage && calculatedTotalPages <= page) {
      calculatedTotalPages = page + 1
    }

    return {
      totalPages: calculatedTotalPages,
      count: calculatedCount,
      hasNextPage: apiPageInfo.hasNextPage || false,
      hasPreviousPage: apiPageInfo.hasPreviousPage || false,
    }
  }, [plansData, plans.length, rowsPerPage, page])

  const totalPages = pageInfo.totalPages
  const totalCount = pageInfo.count

  const handleAddEditPlan = (action: string, plan?: PlanResponseDto | PlanWithBillingDto) => {
    const config = {
      modalHeader: {
        title: action === 'edit' ? 'Edit Plan' : 'Create New Plan',
        subtitle:
          action === 'edit'
            ? 'Please fill the form to edit the Plan.'
            : 'Set up a new plan with storage allocation and pricing settings.',
        showCloseButton: true,
      },
      data: {
        planInfo: plan,
        action: action,
      },
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[634px]',
      modelActive: ModelControlEnum.CREATE_PLAN,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const handleDeletePlan = (plan: PlanResponseDto | PlanWithBillingDto) => {
    const tenantCount = plan.tenantCount || 0
    const canDelete = tenantCount === 0

    const config = {
      modalHeader: {
        title: 'Delete Plan',
        showCloseButton: true,
      },
      message: canDelete
        ? `Are you sure you want to permanently delete '${plan.name}'? This action cannot be undone.`
        : `❌ Cannot delete plan. ${tenantCount} tenant${tenantCount === 1 ? '' : 's'} ${tenantCount === 1 ? 'is' : 'are'} currently using this plan.`,
      icon: canDelete ? '/assets/svg/delete.svg' : '/assets/svg/warning.svg',
      classNames: {
        wrapper: 'w-[500px]',
      },
      actions: [
        {
          label: 'Cancel',
          color: 'default',
        },
        {
          label: 'Delete Plan',
          color: 'danger',
          onPress: async () => {
            // Prevent double-submit using synchronous ref check (prevents race conditions)
            if (inFlightOperations.current.has(plan.id)) {
              return
            }

            // Mark operation as in-flight immediately (synchronous)
            inFlightOperations.current.add(plan.id)
            setLoadingPlanId(plan.id)
            setLoadingAction('delete')

            await mutateAsyncDeletePlan(plan.id, {
              onSuccess: () => {
                toast.success(`Plan '${plan.name}' has been deleted successfully`)
              },
              onError: () => {
                toast.error(`Failed to delete plan '${plan.name}'. Please try again.`)
              },
              onSettled: () => {
                // Remove from in-flight operations
                inFlightOperations.current.delete(plan.id)
                setLoadingPlanId(null)
                setLoadingAction(null)
              },
            })
          },
          disabled: !canDelete,
        },
      ],
    }

    useActiveModelStore.setState({
      data: config,
      width: 'max-w-[650px]',
      modelActive: ModelControlEnum.CONFIRMATION,
      toggleModel: true,
      scrollBehavior: 'outside',
    })
  }

  const renderCell = (data: PlanResponseDto | PlanWithBillingDto, columnKey: Column['uid']) => {
    const plan = data
    // console.log(plan?.qpaServices?.map((m: QpaServiceKey) => m?.serviceKey || ''))

    switch (columnKey) {
      case 'name':
        return (
          <div className="flex flex-col gap-1">
            <p className="text-base font-medium dark:text-white text-black">{plan.name}</p>
            <p className="text-sm dark:text-[#AFAFAF] text-[#262626]">id: {plan.id}</p>
          </div>
        )

      case 'status':
        return (
          <Chip
            className="capitalize rounded font-medium"
            size="sm"
            variant="flat"
            style={{
              backgroundColor: plan.isActive ? '#378A921A' : '#F5A5241A',
              color: plan.isActive ? '#378A92' : '#F5A524',
            }}
          >
            {plan.isActive ? 'Active' : 'Deactivated'}
          </Chip>
        )

      case 'actions': {
        const isLoading = loadingPlanId === plan.id

        return (
          <div className="flex items-center gap-2">
            <Dropdown>
              <DropdownTrigger>
                <Button isIconOnly size="sm" variant="light">
                  <span className="text-2xl">⋮</span>
                </Button>
              </DropdownTrigger>
              <DropdownMenu>
                <DropdownItem key="edit" onPress={() => handleAddEditPlan('edit', plan)}>
                  Edit
                </DropdownItem>

                {plan.isActive ? (
                  <DropdownItem
                    key="deactivate"
                    isDisabled={isLoading && loadingAction === 'deactivate'}
                    onPress={() => handleDeactivatePlan(plan)}
                  >
                    Deactivate
                  </DropdownItem>
                ) : (
                  <DropdownItem
                    key="activate"
                    isDisabled={isLoading && loadingAction === 'activate'}
                    onPress={() => handleActivatePlan(plan)}
                  >
                    Activate
                  </DropdownItem>
                )}
                {!plan.isActive && user?.userRole === UserRoleEnum.SUPER_ADMIN ? (
                  <DropdownItem
                    key="delete"
                    isDisabled={isLoading && loadingAction === 'deactivate'}
                    onPress={() => handleDeletePlan(plan)}
                  >
                    Delete
                  </DropdownItem>
                ) : null}
              </DropdownMenu>
            </Dropdown>
          </div>
        )
      }
      case 'productName':
        return (
          <span className="text-black dark:text-white">
            {'productName' in plan ? String(plan.productName || 'N/A') : 'N/A'}
          </span>
        )
      case 'storageQuotaGb':
        return (
          <span className="text-black dark:text-white">
            {formatStorageQuota(plan.storageQuotaGb)}
          </span>
        )

      case 'serviceUsageQuota':
        return (
          <span className="text-black dark:text-white">
            {/* {formatStorageQuota(plan.serviceUsageQuota)} */}
            {plan.serviceUsageQuota}
          </span>
        )

      case 'audience':
        return (
          <span className="text-black dark:text-white">
            {'audience' in plan ? plan.audience : 'N/A'}
          </span>
        )
      case 'qpaSeedCount':
        return <span className="text-black dark:text-white">{plan.qpaSeedCount || 'N/A'}</span>
      case 'qpaServices': {
        const enabledKeys = plan.qpaServices
          ?.map((s: QpaServiceResponseDto | string) => (typeof s === 'string' ? s : s?.serviceKey))
          .filter(Boolean) as string[] | undefined

        if (!enabledKeys?.length) {
          return <span className="text-default-400 text-xs">N/A</span>
        }

        return (
          <div className="flex flex-wrap gap-1 max-w-[220px] max-h-[72px] overflow-hidden">
            {enabledKeys.map((key: string) => (
              <Chip key={key} className="text-xs h-5 px-1" color="primary" size="sm" variant="flat">
                {key}
              </Chip>
            ))}
          </div>
        )
      }
      case 'createdAt':
        return (
          <span className="text-black dark:text-white">
            {moment(plan.createdAt).format('MM/DD/YYYY')}
          </span>
        )
      default: {
        const value = plan[columnKey as keyof typeof plan]

        return (
          <span className="text-black dark:text-white">
            {typeof value === 'string' || typeof value === 'number' ? value : '-'}
          </span>
        )
      }
    }
  }

  // derive data for the table so row identity changes only when needed
  const tableData = useMemo(
    () =>
      plans.map(p => ({
        ...p,
        __rowLoading: loadingPlanId === p.id ? loadingAction : null, // 'activate' | 'deactivate' | 'delete' | null
      })),
    [plans, loadingPlanId, loadingAction],
  )

  if (isPlansLoading) {
    return (
      <LoadingData description="Please wait while we fetch your plans..." title="Loading Plans" />
    )
  }

  if (plansError) {
    return (
      <ErrorData
        description="An unexpected error occurred while loading plans data."
        title="Error Loading Data"
      />
    )
  }

  return (
    <section className="md:px-[30px] px-4 flex flex-col gap-5">
      <div className=" flex justify-between flex-wrap items-center gap-4">
        <div>
          <h1 className="font-medium text-2xl">Plan Management</h1>
          <p>Manage your subscription plans and pricing</p>
        </div>
        <div className="md:flex items-center hidden  gap-3">
          <Button
            color="primary"
            radius="sm"
            size="lg"
            onPress={() => {
              handleAddEditPlan('create')
            }}
          >
            Create Plan
          </Button>
        </div>
      </div>

      {isMobile ? (
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <Button
              color="primary"
              radius="sm"
              onPress={() => {
                handleAddEditPlan('create')
              }}
            >
              Create Plan
            </Button>
          </div>
          {plans.length === 0 ? (
            <div className="text-center text-[#AFAFAF] py-8">No plans found.</div>
          ) : (
            <ScrollShadow
              hideScrollBar
              className="max-h-[calc(100vh-400px)] overflow-y-auto flex flex-col gap-4 pb-14"
            >
              {plans.map((plan: PlanResponseDto | PlanWithBillingDto) => (
                <PlanCard
                  key={plan.id}
                  loadingAction={loadingAction}
                  loadingPlanId={loadingPlanId}
                  plan={plan}
                  userRole={user?.userRole || ''}
                  onActivate={handleActivatePlan}
                  onDeactivate={handleDeactivatePlan}
                  onDelete={handleDeletePlan}
                  onEdit={handleAddEditPlan}
                />
              ))}
            </ScrollShadow>
          )}
          {plans.length > 0 && (
            <div className="flex justify-between items-center mt-4 gap-2">
              <Dropdown
                size="sm"
                classNames={{
                  content:
                    'bg-white dark:bg-[#343537] border border-gray-200 dark:border-[#707070]',
                }}
              >
                <DropdownTrigger>
                  <Button
                    endContent={<ChevronDownIcon className="text-small" />}
                    size="sm"
                    variant="light"
                  >
                    {rowsPerPage}
                  </Button>
                </DropdownTrigger>
                <DropdownMenu
                  aria-label="Rows per page"
                  selectedKeys={new Set([rowsPerPage.toString()])}
                  selectionMode="single"
                  onAction={(key: Key) => {
                    handleRowsPerPageChange(Number(key))
                  }}
                >
                  <DropdownItem key="10">10</DropdownItem>
                  <DropdownItem key="25">25</DropdownItem>
                  <DropdownItem key="100">100</DropdownItem>
                </DropdownMenu>
              </Dropdown>

              {/* Mobile Pagination */}
              <div className="flex items-center gap-2">
                <Button
                  isDisabled={page <= 1}
                  size="sm"
                  variant="flat"
                  onPress={() => handlePageChange(page - 1)}
                >
                  Previous
                </Button>
                <span className="text-sm text-[#AFAFAF]">
                  Page {page} of {totalPages}
                </span>
                <Button
                  isDisabled={page >= totalPages}
                  size="sm"
                  variant="flat"
                  onPress={() => handlePageChange(page + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <TableComponent
          columns={columns}
          currentPage={page}
          data={tableData}
          disableLocalPagination={true}
          high="h-[calc(100vh-250px)]"
          rowsPerPage={rowsPerPage}
          selectionMode="none"
          showBottomContent={true}
          showTopContent={false}
          sortDescriptor={sortDescriptor}
          total={totalCount}
          totalPages={totalPages}
          trClassName={'border-b dark:border-[#2A2A2A] border-[#ddd]'}
          customRenderCell={
            ((row: TableRow, columnKey: Column['uid']) => {
              // read the ephemeral field from *this* row
              const rowLoadingAction = row.__rowLoading as PlanCardProps['loadingAction']
              const isRowLoading = !!rowLoadingAction

              if (columnKey === 'actions') {
                return (
                  <div className="flex items-center gap-2">
                    <Dropdown>
                      <DropdownTrigger>
                        <Button isIconOnly size="sm" variant="light">
                          <span className="text-2xl">⋮</span>
                        </Button>
                      </DropdownTrigger>
                      <DropdownMenu>
                        <DropdownItem key="edit" onPress={() => handleAddEditPlan('edit', row)}>
                          Edit
                        </DropdownItem>

                        {row.isActive ? (
                          <DropdownItem
                            key="deactivate"
                            isDisabled={isRowLoading && rowLoadingAction === 'deactivate'}
                            endContent={
                              isRowLoading &&
                              rowLoadingAction === 'deactivate' && <Spinner size="sm" />
                            }
                            onPress={() => handleDeactivatePlan(row)}
                          >
                            Deactivate
                          </DropdownItem>
                        ) : (
                          <DropdownItem
                            key="activate"
                            isDisabled={isRowLoading && rowLoadingAction === 'activate'}
                            endContent={
                              isRowLoading &&
                              rowLoadingAction === 'activate' && <Spinner size="sm" />
                            }
                            onPress={() => handleActivatePlan(row)}
                          >
                            Activate
                          </DropdownItem>
                        )}

                        {!row.isActive && user?.userRole === UserRoleEnum.SUPER_ADMIN ? (
                          <DropdownItem
                            key="delete"
                            isDisabled={isRowLoading && rowLoadingAction === 'delete'}
                            endContent={
                              isRowLoading && rowLoadingAction === 'delete' && <Spinner size="sm" />
                            }
                            onPress={() => handleDeletePlan(row)}
                          >
                            Delete
                          </DropdownItem>
                        ) : null}
                      </DropdownMenu>
                    </Dropdown>
                  </div>
                )
              }

              // ...rest of your switch
              return renderCell(row, columnKey)
            }) as unknown as (data: Column, columnKey: Column['uid']) => React.ReactNode
          }
          initialVisibleColumns={[
            'name',
            'storageQuotaGb',
            'serviceUsageQuota',
            'status',
            'productName',
            'createdAt',
            'audience',
            'qpaSeedCount',
            'qpaServices',
            'actions',
          ]}
          onLimitChange={handleRowsPerPageChange}
          onPageChange={handlePageChange}
          onSortChange={handleSortChange}
        />
      )}
    </section>
  )
}

export default PlanManagement
